import React from 'react';
// Constants
import { CampaignVersion } from 'constants/global';
// Helpers
import { parseForInnerHTMLWithBreaks } from 'helpers/textEditor';
import {
  getCustomActivity,
  getCustomBriefActivity,
  getInPersonAppearanceActivity,
  getSABio,
  getSAName,
  getSocialActivity,
  getSocialBoostActivity,
  getSocialShareActivity,
  getSponsoredAppearanceActivity,
  getUgcActivity,
  getVirtualAppearanceActivity,
} from 'helpers/cbActivity';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity_target_SponsoredAppearanceProduct,
} from 'api/campaignV2/types/GetCampaignsV2';
// Ui
import { DetailCard } from 'ui/DetailList/DetailList';
// Components
import SocialActivity from './components/SocialActivity';
// Style
import styles from './CampaignActivityData.module.scss';
import UgcActivity from './components/UgcActivity';
import CustomBriefActivity from './components/CustomBriefActivity';
import SocialBoostActivity from './components/SocialBoostActivity';

type CampaignActivityDataProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  briefActions?: React.ReactNode;
};

const CampaignActivityData = ({
  record,
  briefActions,
}: CampaignActivityDataProps): JSX.Element | null => {
  const socialActivity = getSocialActivity(record);
  const socialShareActivity = getSocialShareActivity(record);
  const sponsoredAppearanceActivity = getSponsoredAppearanceActivity(record);
  const virtualAppearanceActivity = getVirtualAppearanceActivity(record);
  const inPersonAppearanceActivity = getInPersonAppearanceActivity(record);
  const customActivity = getCustomActivity(record);
  const customBriefActivity = getCustomBriefActivity(record);
  const socialBoostActivity = getSocialBoostActivity(record);
  const ugcActivity = getUgcActivity(record);

  const sponsoredAppearanceBio = getSABio(sponsoredAppearanceActivity);
  const sponsoredAppearanceName = getSAName(sponsoredAppearanceActivity);

  const sponsoredAppearanceProduct =
    sponsoredAppearanceActivity?.target?.__typename ===
    'SponsoredAppearanceProduct'
      ? (sponsoredAppearanceActivity?.target as GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity_target_SponsoredAppearanceProduct)
      : undefined;

  const hasProduct =
    sponsoredAppearanceProduct ||
    (socialActivity && socialActivity?.physicalProduct) ||
    (ugcActivity && ugcActivity?.physicalProduct);

  const productType =
    sponsoredAppearanceProduct?.physicalProductType ||
    socialActivity?.physicalProductType ||
    ugcActivity?.physicalProductType;

  const productName =
    sponsoredAppearanceProduct?.productName ||
    socialActivity?.productName ||
    ugcActivity?.productName;

  const productValue =
    sponsoredAppearanceProduct?.productValue ||
    socialActivity?.productValue ||
    ugcActivity?.productValue;

  const isV2Campaign = record.v === CampaignVersion.V2;
  const isV3Campaign = record.v === CampaignVersion.V3;

  const instructions =
    socialActivity?.instructions ||
    socialShareActivity?.instructions ||
    virtualAppearanceActivity?.instructions ||
    sponsoredAppearanceActivity?.target?.instructions ||
    inPersonAppearanceActivity?.instructions ||
    customActivity?.instructions ||
    ugcActivity?.instructions;

  const possibleDates =
    inPersonAppearanceActivity || virtualAppearanceActivity
      ? inPersonAppearanceActivity?.possibleDates ||
        virtualAppearanceActivity?.possibleDates
      : null;

  const description =
    inPersonAppearanceActivity || virtualAppearanceActivity
      ? inPersonAppearanceActivity?.description ||
        virtualAppearanceActivity?.description
      : null;

  const duration =
    inPersonAppearanceActivity || virtualAppearanceActivity
      ? inPersonAppearanceActivity?.duration ||
        virtualAppearanceActivity?.duration
      : null;

  const brandName =
    socialActivity?.brandName ||
    socialShareActivity?.brandName ||
    ugcActivity?.brandName ||
    null;

  const website =
    socialActivity?.website ||
    socialShareActivity?.website ||
    ugcActivity?.website ||
    null;

  const promoCodes = socialActivity?.promoCodes ?? ugcActivity?.promoCodes;
  const promoUrls = socialActivity?.promoUrls ?? ugcActivity?.promoUrls;

  if (customBriefActivity) {
    return (
      <DetailCard title="Campaign brief" actions={briefActions}>
        <div className={styles.root}>
          <CustomBriefActivity customBriefActivity={customBriefActivity} />
        </div>
      </DetailCard>
    );
  }

  if (socialBoostActivity) {
    return (
      <DetailCard title="Campaign brief">
        <div className={styles.root}>
          <SocialBoostActivity socialBoostActivity={socialBoostActivity} />
        </div>
      </DetailCard>
    );
  }

  return (
    <DetailCard title="Campaign brief" actions={briefActions}>
      <div className={styles.root}>
        {brandName && (
          <div>
            <p className={styles.label}>Brand Name</p>
            <p className={styles.value}>{brandName}</p>
          </div>
        )}

        {website && (
          <div className={styles.wide}>
            <p className={styles.label}>Website</p>
            <p className={styles.value}>{website}</p>
          </div>
        )}

        {socialShareActivity?.socialShareDescription && (
          <div className={styles.wide}>
            <p className={styles.label}>Description</p>
            <p
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(
                  socialShareActivity?.socialShareDescription
                ),
              }}
            />
          </div>
        )}

        {sponsoredAppearanceActivity && (
          <div>
            <p className={styles.label}>Name</p>
            <p className={styles.value}>{sponsoredAppearanceName}</p>
          </div>
        )}

        {sponsoredAppearanceActivity && (
          <div className={styles.wide}>
            <p className={styles.label}>Bio</p>
            <p className={styles.value}>{sponsoredAppearanceBio}</p>
          </div>
        )}

        {hasProduct && (
          <>
            <div>
              <p className={styles.label}>Physical Category/Type</p>
              <p className={styles.value}>{`${productType}`}</p>
            </div>

            <div>
              <p className={styles.label}>Product Name</p>
              <p className={styles.value}>{`${productName}`}</p>
            </div>

            <div>
              <p className={styles.label}>Product Value</p>
              <p className={styles.value}>${`${productValue}`}</p>
            </div>
          </>
        )}

        {!!instructions && (
          <div className={styles.wide}>
            <p className={styles.label}>Campaign instructions</p>
            <p
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(instructions),
              }}
            />
          </div>
        )}

        {(socialActivity || ugcActivity) && (
          <div className={styles.wide}>
            <p className={styles.label}>Encouraged</p>
            <p
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(
                  socialActivity?.encouraged ?? ugcActivity?.encouraged ?? 'n/a'
                ),
              }}
            />
          </div>
        )}

        {(socialActivity || ugcActivity) && (
          <div className={styles.wide}>
            <p className={styles.label}>Please avoid</p>
            <p
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(
                  socialActivity?.avoid ?? ugcActivity?.avoid ?? 'n/a'
                ),
              }}
            />
          </div>
        )}

        <div className={styles.wide}>
          <SocialActivity
            socialActivity={socialActivity}
            isV2Campaign={isV2Campaign}
            isV3Campaign={isV3Campaign}
          />
        </div>

        {socialActivity && (
          <div className={styles.wide}>
            <p className={styles.label}>Hashtags</p>
            <p className={styles.value}>
              {socialActivity.hashtags?.join(', ') || 'n/a'}
            </p>
          </div>
        )}

        {socialActivity && (
          <div>
            <p className={styles.label}>Profile mentions</p>
            <p className={styles.value}>
              {socialActivity.profileMentions ?? 'n/a'}
            </p>
          </div>
        )}

        {(socialActivity || ugcActivity) && (
          <div className={styles.wide}>
            <p className={styles.label}>Promotion codes</p>
            {(promoCodes?.length ?? 0) > 0 ? (
              promoCodes?.map((item) => {
                return (
                  <p key={item} className={styles.value}>
                    {item}
                  </p>
                );
              })
            ) : (
              <p className={styles.value}>n/a</p>
            )}
          </div>
        )}

        {(socialActivity || ugcActivity) && (
          <div className={styles.wide}>
            <p className={styles.label}>Promotion urls</p>
            {(promoUrls?.length ?? 0) > 0 ? (
              promoUrls?.map((item) => {
                return (
                  <p key={item} className={styles.value}>
                    {item}
                  </p>
                );
              })
            ) : (
              <p className={styles.value}>n/a</p>
            )}
          </div>
        )}

        {!!productType && (
          <div>
            <p className={styles.label}>Product Type</p>
            <p className={styles.value}>{productType}</p>
          </div>
        )}

        {socialActivity?.quantityInstructions && (
          <div className={styles.wide}>
            <p className={styles.label}>Quantity instructions</p>
            <p className={styles.value}>
              {socialActivity.quantityInstructions}
            </p>
          </div>
        )}

        {socialActivity && (
          <div className={styles.wide}>
            <p className={styles.label}>Script</p>
            <p
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(
                  socialActivity.script ?? 'n/a'
                ),
              }}
            />
          </div>
        )}

        {socialShareActivity?.shareUrl && (
          <div className={styles.wide}>
            <p className={styles.label}>Post Link</p>
            <p className={styles.value}>{socialShareActivity.shareUrl}</p>
          </div>
        )}

        {inPersonAppearanceActivity?.country && (
          <div>
            <p className={styles.label}>Country</p>
            <p className={styles.value}>{inPersonAppearanceActivity.country}</p>
          </div>
        )}

        {inPersonAppearanceActivity && (
          <div>
            <p className={styles.label}>State</p>
            <p className={styles.value}> {inPersonAppearanceActivity.state}</p>
          </div>
        )}

        {inPersonAppearanceActivity && (
          <div>
            <p className={styles.label}>Zip</p>
            <p className={styles.value}> {inPersonAppearanceActivity.zip}</p>
          </div>
        )}
        {inPersonAppearanceActivity && (
          <div>
            <p className={styles.label}>City</p>
            <p className={styles.value}> {inPersonAppearanceActivity.city}</p>
          </div>
        )}
        {inPersonAppearanceActivity && (
          <div className={styles.wide}>
            <p className={styles.label}>Address</p>
            <p className={styles.value}>{inPersonAppearanceActivity.address}</p>
          </div>
        )}

        {inPersonAppearanceActivity && (
          <div>
            <p className={styles.label}>Venue name</p>
            <p className={styles.value}>
              {inPersonAppearanceActivity.venueName}
            </p>
          </div>
        )}

        {inPersonAppearanceActivity && (
          <div>
            <p className={styles.label}>Covered expenses</p>
            <p className={styles.value}>
              {inPersonAppearanceActivity.coveredExpenses}
            </p>
          </div>
        )}

        {!!possibleDates && (
          <div className={styles.wide}>
            <p className={styles.label}>Possible Dates</p>
            <p className={styles.value}>{possibleDates}</p>
          </div>
        )}

        {!!description && (
          <div className={styles.wide}>
            <p className={styles.label}>Description</p>
            <p
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: parseForInnerHTMLWithBreaks(description),
              }}
            />
          </div>
        )}

        {!!duration && (
          <div>
            <p className={styles.label}>Duration</p>
            <p className={styles.value}>{duration}</p>
          </div>
        )}

        {sponsoredAppearanceActivity && (
          <div>
            <p className={styles.label}>Video Type</p>
            <p className={styles.value}>
              {sponsoredAppearanceActivity.videoType}
            </p>
          </div>
        )}

        {sponsoredAppearanceActivity && (
          <div>
            <p className={styles.label}>With Amplify</p>
            <p className={styles.value}>
              {`${sponsoredAppearanceActivity.withAmplify || false}`}
            </p>
          </div>
        )}

        {sponsoredAppearanceActivity?.target && (
          <div className={styles.wide}>
            <p className={styles.label}>Target</p>
            <p className={styles.value}>
              {sponsoredAppearanceActivity.target.type +
                ', id: ' +
                sponsoredAppearanceActivity.target.id}
            </p>
          </div>
        )}

        {ugcActivity && (
          <div className={styles.wide}>
            <UgcActivity UgcActivity={ugcActivity} />
          </div>
        )}
      </div>
    </DetailCard>
  );
};

export default CampaignActivityData;
